import {
	ReactElement,
	Ref,
	useCallback,
	useMemo
} from "react";
import {
	HubCommentSection,
	HubCommentSectionService
} from "client/Features/Hub/HubCommentSection/HubCommentSection";
import { useCommentSection } from "../HubCommentSection/hooks/useCommentSection";
import { getCommentsList } from "client/Api/Communities/communityPostViewer";
import { getCommunityCommentService } from "../Communities/getCommunityService";
import { HubCommentModel, HubCommentPagingInformationModel } from "client/Api/Hub/common";
import { SortDirection } from "client/Api/common";
import { TimeZone } from "client/DateAndTime/timezones";
import { HubCommentOptionsModel } from "client/Api/Hub/common.details";
import { MobileCommentBoxPreservedSpace } from "../HubCommentSection/MobileComment/MobileCommentBoxPreservedSpace";
import { localize } from "client_legacy/ndaw/culture";
import { NoResults } from "client/Components";

export type CommunityPostPageCommentSectionProps = {
	postId: number;
	options: HubCommentOptionsModel;
	totalCommentsCount: number;
	timezone: TimeZone;
	onPostStatisticsUpdate: () => void;
	onMobileCommentIsEditingChanged: (isEditing: boolean) => void;
	ref?: Ref<HTMLDivElement>;
};

export const CommunityPostPageCommentSection = (
	props: CommunityPostPageCommentSectionProps
): ReactElement => {
	const {
		postId,
		options,
		totalCommentsCount,
		timezone,
		onPostStatisticsUpdate,
		onMobileCommentIsEditingChanged,
		ref
	} = props;

	const service = useMemo(
		(): HubCommentSectionService => {
			return getCommunityCommentService(postId);
		},
		[postId]
	);

	const getCommentsListHandler = useCallback(
		(
			pageSize: number,
			after: HubCommentPagingInformationModel | null,
			sortDirection: SortDirection,
			signal: AbortSignal
		): Promise<LazyLoadPageResult<HubCommentModel>> => {
			return getCommentsList(
				postId,
				pageSize,
				after,
				sortDirection,
				signal
			);
		},
		[postId]
	);

	const {
		commentListRef,
		currentUserInfo,
		imageViewerDialog,
		isMobile,
		mobileCommentFormRef,
		mobileMode,
		sortPreference,
		state,
		structuredComments,
		commentDeletedHandler,
		commentReactionHandler,
		commentCreatedHandler,
		commentUpdatedHandler,
		initMobileMode,
		loadMoreHandler,
		mobileModeChangeHandler,
		onSortPreferenceChange,
		viewImageHandler,
		reloadHandler
	} = useCommentSection({
		getCommentsList: getCommentsListHandler,
		sortKey: communityCommentsSortKey,
		onStatisticsUpdate: onPostStatisticsUpdate
	});

	const hasComments: boolean = totalCommentsCount > 0;

	if (
		state.status === "error"
		&& state.items
			.length === 0
	) {
		return (
			<NoResults
				icon="gear"
				title={localize("_NotAbleToRetrieveComments")}
				message={localize("_TheCommentsCouldNotBeRetrieved")}
				onRetry={reloadHandler}
			/>
		);
	}

	return (
		<>
			<HubCommentSection ref={ref}>
				{!isMobile && (
					<HubCommentSection.CommentForm
						service={service}
						onCreated={commentCreatedHandler}
						currentUserInfo={currentUserInfo}
						options={options}
					/>
				)}
				{hasComments && (
					<>
						<HubCommentSection.Header
							total={totalCommentsCount}
							sortPreference={sortPreference}
							onSortPreferenceChange={onSortPreferenceChange}
						/>
						<HubCommentSection.CommentList
							ref={commentListRef}
							comments={structuredComments}
							onStatisticsUpdated={commentReactionHandler}
							onUpdated={commentUpdatedHandler}
							onDeleted={commentDeletedHandler}
							onCreated={commentCreatedHandler}
							isMobile={isMobile}
							mobileModeChangeHandler={mobileModeChangeHandler}
							mobileMode={mobileMode}
							service={service}
							currentUserInfo={currentUserInfo}
							timezone={timezone}
							viewImageHandler={viewImageHandler}
							commentOptions={options}
							renderAfterItems={
								state.status === "error"
									? (
										<NoResults
											icon="gear"
											title={localize("_NotAbleToRetrieveComments")}
											message={localize("_TheCommentsCouldNotBeRetrieved")}
											onRetry={loadMoreHandler}
										/>
									)
									: (
										<HubCommentSection.LoadMore
											variant="load-more-indicator"
											hasMore={state.hasMore}
											isLoading={state.status === "loading"}
											onLoadMore={loadMoreHandler}
										/>
									)
							}
						/>
					</>
				)}
				<MobileCommentBoxPreservedSpace />
				{isMobile && (
					<HubCommentSection.MobileComment
						currentUserInfo={currentUserInfo}
						onCreated={commentCreatedHandler}
						mode={mobileMode}
						onCancel={initMobileMode}
						onUpdated={commentUpdatedHandler}
						ref={mobileCommentFormRef}
						onMobileCommentIsEditingChanged={onMobileCommentIsEditingChanged}
						service={service}
						options={options}
					/>
				)}
			</HubCommentSection>
			{imageViewerDialog}
		</>
	);
};
CommunityPostPageCommentSection.displayName = "CommunityPostPageCommentSection";

export const communityCommentsSortKey = "community:commentsSort";